import React, { ButtonHTMLAttributes, ReactNode } from 'react';

export type MyButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
    /**
     * Button color theme
     * @default 'primary'
     */
    color?:
    | 'primary'
    | 'secondary'
    | 'success'
    | 'danger'
    | 'warning'
    | 'info'
    | 'light'
    | 'dark';
    /**
     * Button style variant
     * @default 'solid'
     */
    variant?: 'solid' | 'outline' | 'text';
    /**
     * Show loading spinner and disable button
     * @default false
     */
    isLoading?: boolean;
    /**
     * Accessible label for SEO and screen readers
     */
    ariaLabel?: string;
    /**
     * Button content
     */
    children: ReactNode;
};

const colorStyles: Record<
    NonNullable<MyButtonProps['color']>,
    Record<NonNullable<MyButtonProps['variant']>, string>
> = {
    primary: {
        solid: 'bg-[#223843] text-white hover:bg-[#98A916] focus:ring-[#223843] border-transparent',
        outline: 'bg-transparent text-[#223843] border border-[#223843] hover:bg-[#F5F5F7] focus:ring-[#223843]',
        text: 'bg-transparent text-[#223843] hover:bg-[#F5F5F7] focus:ring-[#223843]',
    },
    secondary: {
        solid: 'bg-[#606060] text-white hover:bg-[#505050] focus:ring-[#606060] border-transparent',
        outline: 'bg-transparent text-[#606060] border border-[#606060] hover:bg-gray-100 focus:ring-[#606060]',
        text: 'bg-transparent text-[#606060] hover:bg-gray-100 focus:ring-[#606060]',
    },
    success: {
        solid: 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500 border-transparent',
        outline: 'bg-transparent text-green-600 border border-green-600 hover:bg-green-50 focus:ring-green-500',
        text: 'bg-transparent text-green-600 hover:bg-green-50 focus:ring-green-500',
    },
    danger: {
        solid: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500 border-transparent',
        outline: 'bg-transparent text-red-600 border border-red-600 hover:bg-red-50 focus:ring-red-500',
        text: 'bg-transparent text-red-600 hover:bg-red-50 focus:ring-red-500',
    },
    warning: {
        solid: 'bg-yellow-500 text-white hover:bg-yellow-600 focus:ring-yellow-400 border-transparent',
        outline: 'bg-transparent text-yellow-500 border border-yellow-500 hover:bg-yellow-50 focus:ring-yellow-400',
        text: 'bg-transparent text-yellow-500 hover:bg-yellow-50 focus:ring-yellow-400',
    },
    info: {
        solid: 'bg-teal-600 text-white hover:bg-teal-700 focus:ring-teal-500 border-transparent',
        outline: 'bg-transparent text-teal-600 border border-teal-600 hover:bg-teal-50 focus:ring-teal-500',
        text: 'bg-transparent text-teal-600 hover:bg-teal-50 focus:ring-teal-500',
    },
    light: {
        solid: 'bg-[#F5F5F7] text-gray-800 hover:bg-[#EEE9CF] focus:ring-gray-200 border-[#EEE9CF]',
        outline: 'bg-transparent text-gray-800 border border-gray-200 hover:bg-[#F5F5F7] focus:ring-gray-200',
        text: 'bg-transparent text-gray-800 hover:bg-[#F5F5F7] focus:ring-gray-200',
    },
    dark: {
        solid: 'bg-[#2E2E2E] text-white hover:bg-[#1F1F1F] focus:ring-[#2E2E2E] border-transparent',
        outline: 'bg-transparent text-[#2E2E2E] border border-[#2E2E2E] hover:bg-gray-100 focus:ring-[#2E2E2E]',
        text: 'bg-transparent text-[#2E2E2E] hover:bg-gray-100 focus:ring-[#2E2E2E]',
    },
};

const baseClasses =
    'inline-flex items-center justify-center px-4 py-2 border text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 transition';

const spinner = (
    <svg
        className="animate-spin h-5 w-5"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
    >
        <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
        />
        <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8v8H4z"
        />
    </svg>
);

const MyButton: React.FC<MyButtonProps> = ({
    color = 'primary',
    variant = 'solid',
    isLoading = false,
    disabled = false,
    ariaLabel,
    children,
    className = '',
    ...rest
}) => {
    const variantClasses = colorStyles[color][variant];
    const disabledClasses = disabled || isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer';

    return (
        <button
            type="button"
            {...rest}
            className={`${baseClasses} ${variantClasses} ${disabledClasses} ${className}`.trim()}
            disabled={disabled || isLoading}
            aria-label={ariaLabel}
        >
            {isLoading ? spinner : children}
        </button>
    );
};

export default MyButton;